import type { Attachment } from '@trips/shared/schema';
import { useRef, useState } from 'react';
import { addFiles, useFileUrl } from '../../data/files';
import { haptics } from '../../lib/platform';
import { Button, IconButton } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { Icon } from '../../ui/Icon';
import { toast } from '../../ui/Toast';
import s from './Photos.module.css';

function Thumb({ att, onOpen }: { att: Attachment; onOpen: () => void }) {
  const url = useFileUrl(att, 'thumb');
  return (
    <button
      type="button"
      className={s.thumb}
      onClick={onOpen}
      aria-label={att.caption || 'Open photo'}
    >
      {url ? (
        <img src={url} alt="" loading="lazy" decoding="async" />
      ) : (
        <span className={s.loading} />
      )}
    </button>
  );
}

function Viewer({
  photos,
  index,
  onClose,
}: {
  photos: Attachment[];
  index: number;
  onClose: () => void;
}) {
  const [i, setI] = useState(index);
  const att = photos[i]!;
  const url = useFileUrl(att, 'large');
  const thumb = useFileUrl(att, 'thumb');
  return (
    <div
      className={s.viewer}
      role="dialog"
      aria-modal="true"
      aria-label="Photo"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
        if (e.key === 'ArrowRight') setI((v) => Math.min(photos.length - 1, v + 1));
        if (e.key === 'ArrowLeft') setI((v) => Math.max(0, v - 1));
      }}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: the viewer takes keyboard focus for arrow keys
      tabIndex={0}
      ref={(el) => el?.focus()}
    >
      <div className={s.viewerBar}>
        <span className="t-footnote num">
          {i + 1} of {photos.length}
        </span>
        <IconButton icon="x" label="Close" onClick={onClose} variant="sky" />
      </div>
      <img src={url ?? thumb ?? ''} alt={att.caption || ''} className={s.full} />
      {photos.length > 1 && (
        <div className={s.viewerNav}>
          <IconButton
            icon="caret-left"
            label="Previous photo"
            variant="sky"
            disabled={i === 0}
            onClick={() => setI(i - 1)}
          />
          <IconButton
            icon="caret-right"
            label="Next photo"
            variant="sky"
            disabled={i === photos.length - 1}
            onClick={() => setI(i + 1)}
          />
        </div>
      )}
    </div>
  );
}

export function Photos({
  ownerType,
  ownerId,
  photos,
}: {
  ownerType: 'trip' | 'item';
  ownerId: string;
  photos: Attachment[];
}) {
  const input = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);

  const take = async (files: FileList | File[] | null) => {
    const list = [...(files ?? [])].filter(
      (f) => f.type.startsWith('image/') || f.type === 'application/pdf',
    );
    if (!list.length) return;
    setBusy(true);
    try {
      const n = await addFiles(ownerType, ownerId, list);
      void haptics.notify('success');
      toast(n === 1 ? 'Photo added' : `${n} photos added`);
    } catch {
      toast("Those photos couldn't be added. Try again.", { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: a drop target for files; the Add button is the keyboard path
    <div
      className={`${s.photos} ${dragging ? s.dragging : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        void take(e.dataTransfer.files);
      }}
    >
      <input
        ref={input}
        type="file"
        accept="image/*,application/pdf"
        multiple
        hidden
        onChange={(e) => {
          void take(e.target.files);
          e.target.value = '';
        }}
      />
      {photos.length === 0 && ownerType === 'item' ? (
        <button
          type="button"
          className={s.addRow}
          onClick={() => input.current?.click()}
          disabled={busy}
        >
          <Icon name="paperclip" size={18} />
          <span>Add a boarding pass, ticket or photo</span>
        </button>
      ) : photos.length === 0 ? (
        <EmptyState
          icon="images"
          title="Add photos from this trip"
          action={
            <Button icon="camera" onClick={() => input.current?.click()} disabled={busy}>
              Add photos
            </Button>
          }
        >
          <p>
            They're resized on this device before upload and sorted by when they were taken. On a
            computer, drop them here.
          </p>
        </EmptyState>
      ) : (
        <div className={s.grid}>
          {photos.map((p, i) => (
            <Thumb key={p.id} att={p} onOpen={() => setOpen(i)} />
          ))}
          <button
            type="button"
            className={`${s.thumb} ${s.add}`}
            onClick={() => input.current?.click()}
            disabled={busy}
          >
            <Icon name="plus" size={24} />
            <span className="t-caption">Add</span>
          </button>
        </div>
      )}
      {open !== null && <Viewer photos={photos} index={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
